import { BALANCE } from '@2k48/shared';
import { renderLoop } from '../RenderLoop';
import { emitDebugEffect, type DebugEffect } from './effects';
import { metrics, type MetricsSnapshot } from './metrics';

/**
 * The `?debug=1` overlay.
 *
 * Present from day one because "the game feels bad" is not an actionable bug
 * report. Frame time next to patch rate next to clock RTT turns it into one:
 * a 40ms frame with a quiet socket is the renderer, an 8ms frame with a 300ms
 * RTT is the network, and a rising node count with steady frames is a leak that
 * has not started hurting yet.
 *
 * It updates four times a second, not sixty. An overlay that rewrites its own
 * DOM every frame is itself a source of jank, which would make it lie about
 * exactly the thing it is there to measure.
 */

const FIELDS: readonly { key: keyof MetricsSnapshot; label: string; suffix?: string }[] = [
  { key: 'fps', label: 'FPS' },
  { key: 'frameMs', label: 'FRAME', suffix: 'MS' },
  { key: 'worstFrameMs', label: 'WORST', suffix: 'MS' },
  { key: 'droppedFrames', label: 'OVER' },
  { key: 'nodeCount', label: 'NODES' },
  { key: 'poolBusy', label: 'FX' },
  // Stays at 0 until the effect budget actually bites. A non-zero reading here
  // is the evidence that would justify adaptive degradation; without it there
  // is nothing to degrade.
  { key: 'effectsRefused', label: 'FXCUT' },
  { key: 'patchesPerSecond', label: 'PATCH', suffix: '/S' },
  { key: 'clockOffsetMs', label: 'OFFSET', suffix: 'MS' },
  { key: 'clockRttMs', label: 'RTT', suffix: 'MS' },
  { key: 'clockJitterMs', label: 'JIT', suffix: 'MS' },
];

const EFFECTS: readonly { effect: DebugEffect; label: string }[] = [
  { effect: 'attack-8', label: 'ATK 8' },
  { effect: 'attack-64', label: 'ATK 64' },
  { effect: 'power-512', label: '512' },
  { effect: 'power-1024', label: '1024' },
  { effect: 'power-2048', label: '2048' },
  { effect: 'hit', label: 'HIT' },
  { effect: 'ko', label: 'K.O.' },
];

export class DebugOverlay {
  private readonly root: HTMLElement;
  private readonly values = new Map<keyof MetricsSnapshot, HTMLElement>();
  private unsubscribe: (() => void) | null = null;
  private lastPaint = 0;

  constructor(container: HTMLElement = document.body) {
    this.root = document.createElement('div');
    this.root.className = 'debug-overlay';

    for (const field of FIELDS) {
      const row = document.createElement('div');
      row.className = 'debug-overlay__row';

      const label = document.createElement('span');
      label.className = 'debug-overlay__label';
      label.textContent = field.label;

      const value = document.createElement('span');
      value.className = 'debug-overlay__value';
      value.textContent = '-';

      row.append(label, value);
      this.root.appendChild(row);
      this.values.set(field.key, value);
    }

    const controls = document.createElement('div');
    controls.className = 'debug-overlay__controls';
    controls.setAttribute('aria-label', 'Pruebas de efectos de combate');
    for (const item of EFFECTS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = item.label;
      button.title = `Probar ${item.effect}`;
      button.addEventListener('click', () => emitDebugEffect(item.effect));
      controls.appendChild(button);
    }
    this.root.appendChild(controls);

    container.appendChild(this.root);

    this.unsubscribe = renderLoop.subscribe((elapsedMs, deltaMs) => {
      metrics.recordFrame(elapsedMs, deltaMs);
      if (elapsedMs - this.lastPaint < 250) return;
      this.lastPaint = elapsedMs;
      this.paint(metrics.read());
    });
  }

  private paint(snapshot: MetricsSnapshot): void {
    for (const field of FIELDS) {
      const node = this.values.get(field.key);
      if (!node) continue;
      const raw = snapshot[field.key];
      node.textContent = `${raw}${field.suffix ?? ''}`;
    }

    // The two numbers that decide whether it feels smooth. The average is
    // judged against one refresh interval (a steady 60fps averages 16.7ms,
    // not 16); the worst frame against the heavy-effect ceiling.
    this.tint('frameMs', snapshot.frameMs, BALANCE.perf.vsyncMs + 0.5);
    this.tint('worstFrameMs', snapshot.worstFrameMs, BALANCE.perf.heavyFrameBudgetMs);
  }

  private tint(key: keyof MetricsSnapshot, value: number, budget: number): void {
    const node = this.values.get(key);
    if (!node) return;
    node.dataset.state = value > budget ? 'over' : 'ok';
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.root.remove();
  }
}
